import './feature-base.scss';

import { ElementType } from 'react';
import { z } from 'zod';

import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.js';
import { Link } from '@sola/ark-core/atoms/link/link.js';
import { LogoChooser } from '@sola/ark-core/atoms/logos/logos.js';
import {
  Prominence,
  Sizes,
  Text,
  Variants,
} from '@sola/ark-core/atoms/text/text.js';
import { cn } from '@sola/ark-core/utils/helpers.js';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import { IconNamesSafe } from '@sola/schema/lib/icons/icon-names.js';
import { FlexibleContentProps } from '@sola/schema/lib/schema.js';

import { Image } from '../../atoms/image/image';
import { Blockquote } from '../blockquote/blockquote';
import {
  FeatureButtons,
  FeatureButtonsProps,
} from '../feature-buttons/feature-buttons';

export const FeatureBaseProps = FlexibleContentProps.extend({
  ...FeatureButtonsProps.shape,
  icon: IconNamesSafe.optional(),
  imagePlacement: z.enum(['left', 'right']).optional(),
  titleVariant: z.enum(['heading', 'label', 'body']).optional(),
  bodyVariant: z.enum(['label', 'body']).optional(),
  buttonVariant: z.enum(['default', 'stacked', 'grow', 'noWrap']).optional(),
  variant: z
    .enum([
      'standard',
      'iconRow',
      'centered',
      'stat',
      'listItem',
      'quotation',
      'quotationCentered',
    ])
    .optional(),
  variantSpacing: z.enum(['card', 'section', 'default']).optional(),
  variantResponsive: z.boolean().optional(),
  titleSize: z.enum(['xs', 'sm', 'md', 'lg', 'xl', 'xxl']).optional(),
  bodySize: z.enum(['xs', 'sm', 'md', 'lg', 'inherit']).optional(),
  buttonSize: z.enum(['sm', 'md', 'lg', 'xl']).optional(),
  inline: z.boolean().optional(),
  className: z.string().optional(),
  isClickable: z.boolean().optional(),
  hasPlaceholder: z.boolean().optional(),
  titleAsLink: z.boolean().optional(),
  buttonsAsLinks: z.boolean().optional(),
});
export type FeatureBaseProps = z.infer<typeof FeatureBaseProps>;

const getSpacingClasses = (
  sizeTitle: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl',
  sizeBody: 'xs' | 'sm' | 'md' | 'lg' | 'inherit',
  sizeButton: 'sm' | 'md' | 'lg' | 'xl',
  variantTitle: 'heading' | 'label' | 'body',
  variantBody: 'body' | 'label',
  variantSpacing: 'card' | 'section' | 'default',
) => {
  let classNameHeaderSpacing;
  let classNameContentSpacing;

  if (variantTitle === 'label' && variantBody === 'body') {
    classNameHeaderSpacing = 'gap-snug-xl';
    classNameContentSpacing = 'gap-comfy-lg';
    if (['xs', 'sm'].includes(sizeTitle)) {
      classNameHeaderSpacing = 'gap-comfy-sm';
    }
    if (['xs', 'sm'].includes(sizeBody)) {
      classNameContentSpacing = 'gap-comfy-md';
    }
    return {
      classNameHeaderSpacing,
      classNameContentSpacing,
    };
  }

  if (variantTitle === 'label') {
    classNameHeaderSpacing = 'gap-snug-xl';
    classNameContentSpacing = 'gap-snug-xl';
    return {
      classNameHeaderSpacing,
      classNameContentSpacing,
    };
  }

  const variantSpacingHeading = variantSpacing === 'card' ? '@sm' : '@lg';
  const variantSpacingContent = variantSpacing === 'card' ? '@sm' : '@md';
  classNameHeaderSpacing = `gap-comfy-xs ${variantSpacingHeading}:gap-comfy-sm`;
  classNameContentSpacing = `gap-comfy-md ${variantSpacingContent}:gap-comfy-lg`;
  if (variantSpacing === 'card' && sizeButton === 'lg') {
    classNameContentSpacing += ` @lg:gap-comfy-xl`;
  }

  classNameHeaderSpacing = 'gap-snug-xl';
  if (['xs', 'sm'].includes(sizeBody)) {
    classNameHeaderSpacing = 'gap-snug-lg';
    classNameContentSpacing = 'gap-comfy-md';
  }
  if (['xl'].includes(sizeTitle)) {
    classNameContentSpacing = `gap-comfy-lg ${variantSpacingContent}:gap-comfy-xl`;
  }
  return {
    classNameHeaderSpacing,
    classNameContentSpacing,
  };
};

export const FeatureBase: React.FC<
  FeatureBaseProps & {
    programmaticButtons?: React.ReactNode | React.ReactNode[];
    handleTitleClick?: () => void;
  }
> = ({
  seoLevel = 2,
  icon,
  media,
  subtitle,
  title,
  titleSize = 'md',
  titleVariant = 'heading',
  body,
  bodySize = 'md',
  bodyVariant = 'body',
  buttonSize = 'lg',
  variant = 'standard',
  variantSpacing = 'default',
  variantResponsive = false,
  inline = false,
  className,
  quotation,
  logo,
  programmaticButtons,
  isClickable,
  hasPlaceholder,
  alignment,
  ...props
}) => {
  const TitleTag = `h${seoLevel}` as ElementType;
  const variantClass = {
    standard: 'flex-col gap-comfy-sm text-left items-start',
    iconRow: `${
      variantResponsive ? 'flex-col @lg:flex-row' : 'flex-row'
    } gap-comfy-sm`,
    centered: `${
      variantResponsive
        ? '@md:items-center @md:text-center'
        : 'items-center text-center'
    } flex-col gap-snug-xl`,
    quotationCentered: `${
      variantResponsive
        ? '@md:items-center @md:text-center'
        : 'items-center text-center'
    } flex-col gap-snug-xl`,
    quotation: 'flex-col gap-comfy-sm text-left items-start',
  };

  const spacingClasses = getSpacingClasses(
    titleSize,
    bodySize,
    buttonSize,
    titleVariant,
    bodyVariant,
    variantSpacing,
  );

  const titleAsLink = variant === 'listItem';
  const hasTitleGroup = title || subtitle;

  const hasButtons =
    props.primaryCTA ||
    props.secondaryCTA ||
    props.tertiaryCTA ||
    programmaticButtons;

  return (
    <div
      className={cn(
        'FeatureBase w-full flex',
        `FeatureBase--${variant}`,
        variantClass[variant as keyof typeof variantClass],
        {
          'FeatureBase--inline': inline,
          'is-responsive': variantResponsive,
        },
        className,
      )}
    >
      {icon && !isClickable && !titleAsLink && (
        <div className="FeatureBase__icon-container">
          <div className="FeatureBase__icon">
            <ArkIcon
              iconName={icon}
              size="24"
              className="FeatureBase__icon-svg"
            />
          </div>
        </div>
      )}

      {/* @TODO: maybe support different sizes */}
      {logo && (
        <div className="FeatureBase__logo">
          <LogoChooser
            name={logo.logo || 'compassion'}
            color="full"
            height={64}
            className="is-full"
          />
          <LogoChooser
            name={logo.logo || 'compassion'}
            color="white"
            height={64}
            className="is-white"
          />
        </div>
      )}

      {!logo &&
        !icon &&
        !['quotation', 'quotationCentered'].includes(variant) &&
        (media?.image || hasPlaceholder) && (
          <Image
            className="mb-snug-md FeatureBase__image"
            image={media?.image}
            aspectRatio={media?.image?.ratio || '4-3'}
            objectFit={
              media?.image?.ratio === 'contain' ? 'contain' : undefined
            }
            objectPosition={variant === 'centered' ? 'center' : 'left'}
            hasPlaceholder
            priority={seoLevel === 1}
          />
        )}

      <div
        className={cn(
          `FeatureBase__content w-full flex flex-col`,
          spacingClasses.classNameContentSpacing,
        )}
      >
        <div
          className={cn(
            'FeatureBase__text flex flex-col',
            spacingClasses.classNameHeaderSpacing,
          )}
        >
          {hasTitleGroup && (
            <div className="FeatureBase__title-group">
              {variant !== 'stat' && subtitle && (
                <div className="FeatureBase__eyebrow mb-snug-lg">
                  <Text
                    prominence={Prominence.Potent}
                    size={Sizes.Medium}
                    variant={Variants.Label}
                  >
                    {subtitle}
                  </Text>
                </div>
              )}
              {title && titleAsLink && props.primaryCTA && (
                <LinkStyle variant="primary" noUnderline inline>
                  <Link
                    href={props.primaryCTA?.link}
                    onClick={props.handleTitleClick}
                  >
                    <Text
                      asChild
                      prominence={
                        (titleVariant !== 'heading' && Prominence.Potent) ||
                        undefined
                      }
                      size={titleSize as Sizes}
                      variant={titleVariant as Variants}
                    >
                      <TitleTag className="FeatureBase__title m-none">
                        {title}
                      </TitleTag>
                    </Text>
                  </Link>
                </LinkStyle>
              )}
              {title && !titleAsLink && (
                <Text
                  asChild
                  prominence={
                    (titleVariant !== 'heading' && Prominence.Potent) ||
                    undefined
                  }
                  size={titleSize as Sizes}
                  variant={titleVariant as Variants}
                >
                  <TitleTag className="FeatureBase__title m-none">
                    {title}
                  </TitleTag>
                </Text>
              )}
              {variant === 'stat' && subtitle && (
                <Text
                  prominence={Prominence.Potent}
                  size={Sizes.Large}
                  variant={Variants.Label}
                >
                  <div className="FeatureBase__subtitle">{subtitle}</div>
                </Text>
              )}
              {isClickable && (
                <ArkIcon
                  iconName="arrow-up-right"
                  className="FeatureBase__arrow"
                />
              )}
            </div>
          )}
          {body && (
            <Text
              asChild
              size={bodySize as Sizes}
              variant={bodyVariant as Variants}
            >
              <div className={cn('FeatureBase__body')}>{body}</div>
            </Text>
          )}
          {quotation?.text && (
            <div
              className={cn('text-left', {
                'my-comfy-sm': title || subtitle || body,
              })}
            >
              <Blockquote
                quotation={{
                  ...quotation,
                  image: ['quotation', 'quotationCentered'].includes(variant)
                    ? media?.image
                    : quotation.image,
                }}
                size={bodySize}
              />
            </div>
          )}
        </div>

        {hasButtons && !isClickable && !titleAsLink && (
          <div className={cn('FeatureBase__actions')}>
            <FeatureButtons
              {...props}
              buttonSize={buttonSize}
              variant={props.buttonVariant}
              alignment={
                alignment || (variant === 'centered' ? 'center' : 'left')
              }
              inline
              isLinks={props.buttonsAsLinks}
            >
              {programmaticButtons}
            </FeatureButtons>
          </div>
        )}
      </div>
    </div>
  );
};
